// Single source of knowledge about the player's cash currency (#831).
// Backend switches cash from USDT to USDC in place; the front ships first,
// so both tickers count as cash until the USDT tolerance is removed here.

// The one label for the player's cash; amounts always carry this ticker.
export const CASH_TICKER = "USDC";
const LEGACY_CASH_TICKER = "USDT";
const CASH_TICKERS = [LEGACY_CASH_TICKER, CASH_TICKER];

type TickerLike =
  | string
  | { short_name?: string; ticker?: { short_name?: string } }
  | null
  | undefined;

const shortNameOf = (tickerLike: TickerLike): string | undefined => {
  if (typeof tickerLike === "string") return tickerLike;
  return tickerLike?.ticker?.short_name ?? tickerLike?.short_name;
};

export const isCash = (tickerLike: TickerLike): boolean => {
  const shortName = shortNameOf(tickerLike);
  return (
    typeof shortName === "string" &&
    CASH_TICKERS.includes(shortName.toUpperCase())
  );
};

type WalletRow = { ticker?: { short_name?: string }; amount?: number | string };

// Sums rather than picks one row, so the result does not depend on which
// cash row the backend lists first.
export const cashBalance = (
  wallets: WalletRow[] | null | undefined
): number => {
  if (!Array.isArray(wallets)) return 0;
  return wallets.reduce((sum, wallet) => {
    const amount = Number(wallet?.amount);
    return isCash(wallet) && Number.isFinite(amount) ? sum + amount : sum;
  }, 0);
};

export const baseOf = (pair: string | undefined): string | undefined =>
  pair?.split("/")[0];

// The cash side of a pair comes from the API name, so orders carry the ticker
// the backend currently trades in (USDT before the switch, USDC after).
export const quoteOf = (pair: string | undefined): string | undefined =>
  pair?.split("/")[1];

// A pair is named by its data, so until the backend switches the player sees
// BTC/USDT next to amounts already labelled USDC. That is intended: the front
// ships first. The cash label only fills in when the name has no quote.
export const pairLabelOf = (
  base: string | undefined,
  pairName?: string
): string =>
  base
    ? `${base.toUpperCase()}/${(quoteOf(pairName) || CASH_TICKER).toUpperCase()}`
    : "";
